import type { TAssessmentSectionConfig, TAssessmentTemplateConfig } from '../models';
import type { TChangeImplementationPlanItem } from '../../change/types';

// Keep the demo focused on the decisions that drive implementation. IDs remain
// stable so organization-provided answers and activities retain their links.
const DEMO_SECTIONS = [
  {
    ids: ['sec-supplier-qualification'],
    title: 'Supplier qualification',
    description: 'Review the new supplier and available documentation.',
    fields: ['newSupplier'],
    questions: ['supplierAlreadyApproved', 'technicalPackageAvailable'],
  },
  {
    ids: ['sec-component-equivalence', 'sec-biological-evaluation'],
    title: 'Component and patient safety',
    description: 'Review component equivalence and patient-contacting materials.',
    fields: ['newComponent'],
    questions: ['componentFunctionUnchanged', 'patientContactingMaterialChanged'],
  },
  {
    ids: ['sec-risk-management', 'sec-verification-validation'],
    title: 'Risk and verification',
    description: 'Identify any risk-file updates and verification activities.',
    fields: [],
    questions: ['riskManagementUpdateRequired', 'comparativeVerificationRequired'],
  },
  {
    ids: ['sec-regulatory-udi'],
    title: 'Regulatory impact',
    description: 'Review UDI impact and any external notification needs.',
    fields: [],
    questions: ['udiImpact', 'externalNotificationImpact'],
  },
];

export const makeAssessmentSectionOptional = (section: TAssessmentSectionConfig): TAssessmentSectionConfig => ({
  ...section,
  questions: section.questions.map((question) => ({
    ...question,
    required: false,
    requiredWhen: undefined,
    rationaleRequiredWhen: undefined,
    allowsComment: false,
  })),
});

export const simplifyAssessmentTemplate = (
  template: TAssessmentTemplateConfig,
  implementationPlan: TChangeImplementationPlanItem[] = []
): TAssessmentTemplateConfig => {
  let sections = template.assessmentSections;
  let fields = template.customFields;
  if (template.id === 'supplier-component-change') {
    const groupedIds = new Set(DEMO_SECTIONS.flatMap((group) => group.ids));
    const compact = DEMO_SECTIONS.flatMap((group) => {
      const source = sections.find((section) => section.id === group.ids[0]);
      if (!source) return [];
      const questions = sections
        .filter((section) => group.ids.includes(section.id))
        .flatMap((section) => section.questions)
        .filter((question) => group.questions.includes(question.id))
        .map((question) => ({ ...question, visibleWhen: undefined }));
      return [
        { ...source, title: group.title, description: group.description, customFieldIds: group.fields, questions },
      ];
    });
    sections = [...compact, ...sections.filter((section) => !groupedIds.has(section.id))];
    const fieldIds = new Set(sections.flatMap((section) => section.customFieldIds));
    fields = fields.filter((field) => fieldIds.has(field.id));
  }
  const activities = template.preliminaryActivities.map((activity) => {
    const matchingProposal = implementationPlan.find((item) =>
      item.implementationProposal?.diffs?.some((diff) =>
        Boolean(diff.document && activity.linkedObject?.includes(diff.document.split(' - ')[0]))
      )
    )?.implementationProposal;
    const proposal = activity.implementationProposal ?? matchingProposal;
    return {
      ...activity,
      implementationProposal: proposal
        ? {
            ...proposal,
            triggerLabel: 'Propose solution',
            diffs: proposal.diffs?.map((diff) => ({
              ...diff,
              documentId: activity.documents?.[0]?._id ?? diff.documentId,
            })),
          }
        : undefined,
    };
  });
  const candidateActivities: Record<string, string> = {
    'cao-supplier-qa': 'pa-1',
    'cao-device-description': 'pa-2',
    'cao-risk-file': 'pa-3',
    'cao-biological-evaluation': 'pa-4',
    'cao-verification': 'pa-5',
    'cao-ifu-labeling': 'pa-6',
  };
  const candidates =
    template.id === 'supplier-component-change'
      ? template.candidateAffectedObjects.filter(
          (candidate) => candidateActivities[candidate.id] || candidate.documents?.length
        )
      : template.candidateAffectedObjects;
  return {
    ...template,
    customFields: fields.map((field) => ({ ...field, required: false, requiredWhen: undefined })),
    assessmentSections: sections.map(makeAssessmentSectionOptional),
    preliminaryActivities: activities,
    candidateAffectedObjects: candidates.map((candidate) => ({
      ...candidate,
      documents:
        candidate.documents ??
        activities.find((activity) => activity.id === candidateActivities[candidate.id])?.documents,
    })),
  };
};
